// ============================================================================
// INVENTORY ITEM MODAL
// Create/Edit modal for inventory items (products, services, bundles)
// Uses inventory-specific fields: SKU, barcode, pricing, stock, warehouses
// Date: December 18, 2025
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/asset-inventory-management/inventory.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/asset-inventory-management/inventory)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Package, Briefcase, Layers, Loader2, DollarSign, Tag, Barcode, Box, Scale, AlertTriangle, Percent, Eye, UserRound, Plus, Check, ChevronRight, ChevronLeft, ClipboardCheck, Image as ImageIcon, FileText, Truck, Users, Upload } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import WorkItemSuppliersPanel from '@/components/inventory/WorkItemSuppliersPanel';
import WorkItemCustomerPricingPanel from '@/components/inventory/WorkItemCustomerPricingPanel';
import ProductImagesTab from '@/components/tabs/ProductImagesTab';
import MarketingMaterialsPanel from '@/components/finance/modals/MarketingMaterialsPanel';
import ContactSearchFilter, { type ContactOption } from '@/components/filters/ContactSearchFilter';
import ModalFooter from '@/components/shared/ModalFooter';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { notify } from '@/lib/notifications';

// ============================================================================
// STATIC FILE SERVER (photo uploads)
// ============================================================================

const STATIC_FILE_SERVER_BASE = 'https://staticfileserver.totalaccess.co.za';
const STATIC_FILE_API_KEY = process.env.NEXT_PUBLIC_STATIC_FILE_SERVER_API_KEY;

async function uploadProductPhoto(file: File, folder: string): Promise<string | null> {
  if (!STATIC_FILE_API_KEY) {
    console.error('Static file server API key is not configured (NEXT_PUBLIC_STATIC_FILE_SERVER_API_KEY)');
    return null;
  }
  try {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('folder', folder);
    const response = await fetch(`${STATIC_FILE_SERVER_BASE}/upload`, {
      method: 'POST',
      headers: { 'X-API-Key': STATIC_FILE_API_KEY },
      body: uploadFormData,
    });
    if (response.ok) {
      const data = await response.json();
      return data.directUrl || data.url || (data.publicUrl ? `${STATIC_FILE_SERVER_BASE}${data.publicUrl}` : null);
    }
    return null;
  } catch {
    return null;
  }
}

// ============================================================================
// LOCAL STORAGE DRAFT PERSISTENCE
// ============================================================================

// Drafts are scoped per host context + subtype so a product draft started in
// Operations doesn't surface when creating a service from Finance.
const DRAFT_STORAGE_PREFIX = 'ta_inventory_item_draft';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // discard drafts older than a week

interface InventoryDraft {
  formData: InventoryItemData;
  currentStep: InventoryStep;
  savedAt: string;
}

function draftStorageKey(moduleContext: string, subtype: InventorySubtype): string {
  return `${DRAFT_STORAGE_PREFIX}:${moduleContext}:${subtype}`;
}

function saveInventoryDraft(key: string, draft: Omit<InventoryDraft, 'savedAt'>): void {
  try {
    const payload: InventoryDraft = { ...draft, savedAt: new Date().toISOString() };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // localStorage may be unavailable (private mode, quota) — silent fail
  }
}

function readInventoryDraft(key: string): InventoryDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InventoryDraft;
    if (!parsed || !parsed.formData || !parsed.formData.item_subtype) {
      return null;
    }
    // Expire stale drafts — an abandoned draft from weeks ago is confusing to resume.
    const savedAtMs = Date.parse(parsed.savedAt || '');
    if (!Number.isFinite(savedAtMs) || Date.now() - savedAtMs > DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Find the most recent draft across subtypes for this context — the subtype
// pill the user reopens under may differ from the one the draft was saved as.
function loadInventoryDraft(moduleContext: string): { draft: InventoryDraft; key: string } | null {
  const subtypes: InventorySubtype[] = ['product', 'service', 'bundle'];
  let newest: { draft: InventoryDraft; key: string } | null = null;

  for (const subtype of subtypes) {
    const key = draftStorageKey(moduleContext, subtype);
    const draft = readInventoryDraft(key);
    if (!draft) continue;

    if (!newest || Date.parse(draft.savedAt) > Date.parse(newest.draft.savedAt)) {
      newest = { draft, key };
    }
  }

  return newest;
}

function clearInventoryDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // silent
  }
}

export type InventorySubtype = 'product' | 'service' | 'bundle';

type InventoryStep = 'basic' | 'pricing' | 'stock' | 'details' | 'images' | 'materials' | 'suppliers' | 'customer-pricing' | 'review';

interface ContactListRow {
  id: number;
  company_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  currency?: string | null;
  contact_type?: string | null;
}

export interface InventoryItemData {
  id?: string;
  title: string;
  description?: string;
  item_type: 'inventory';
  item_subtype: InventorySubtype;
  status: string;
  // Core inventory fields
  sku?: string;
  barcode?: string;
  category_id?: number;
  category_name?: string;
  // Pricing
  cost_price?: number;
  selling_price?: number;
  currency?: string;
  tax_rate_id?: number;
  tax_rate?: number;
  // Customer-specific pricing
  contact_id?: number;
  contact_name?: string;
  customer_pricing?: Array<{ contact_id: number; contact_name?: string; sku?: string; selling_price?: number }>;
  // Stock settings (products only)
  track_inventory?: boolean;
  unit_of_measure?: string;
  reorder_level?: number;
  reorder_quantity?: number;
  valuation_method?: 'fifo' | 'weighted_average' | 'lifo' | 'standard' | 'specific';
  // Tracking options
  is_serialized?: boolean;
  is_batch_tracked?: boolean;
  has_expiry?: boolean;
  // Product details
  manufacturer?: string;
  brand?: string;
  weight?: number;
  // Photo (uploaded to static file server)
  photo_url?: string | null;
  // Supplier
  supplier_id?: number;
  supplier_name?: string;
  supplier_sku?: string;
  // Custom fields for flexibility
  custom_fields?: Record<string, unknown>;
}

interface InventoryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: InventoryItemData) => Promise<void>;
  existingItem?: InventoryItemData | null;
  defaultSubtype?: InventorySubtype;
  /** Initial value for the "Track inventory" toggle when creating a new item. */
  defaultTrackInventory?: boolean;
  moduleContext?: 'operations' | 'accounts';
  defaultContactId?: number;
  defaultContactName?: string;
  /** Optional override to align the modal with the host workspace accent. */
  accentColor?: string;
  /** Stacking for the full-screen overlay (Header uses up to z-[120]). */
  overlayZIndexClassName?: string;
  /** Stacking for the modal panel when placement is 'right'. Defaults to one level above overlay if omitted. */
  panelZIndexClassName?: string;
  readOnly?: boolean;
  modalSize?: 'default' | 'search-match';
  placement?: 'center' | 'right';
}

interface Category {
  id: number;
  category_name: string;
  category_code?: string | null;
  applies_to?: InventorySubtype | 'all' | null;
  parent_category_id?: number | null;
}

interface TaxRate {
  id: number;
  name: string;
  rate: number;
}

interface Warehouse {
  id: number;
  name: string;
  code: string;
  is_default: boolean;
}

interface CurrencyAmountInputProps {
  symbol: string;
  value: number | '';
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  prefixClassName?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  disabled?: boolean;
}

const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  ZAR: 'R',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const CURRENCY_NUMBER_FORMATTER = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function getCurrencySymbol(currency?: string) {
  return CURRENCY_SYMBOL_MAP[currency || 'ZAR'] || currency || 'R';
}

function formatCurrencyInputValue(value?: number | '') {
  if (value === '' || value == null || Number.isNaN(Number(value))) {
    return '';
  }

  return CURRENCY_NUMBER_FORMATTER.format(Number(value));
}

function parseCurrencyInputValue(value: string): number | undefined {
  const normalized = value.replace(/,/g, '').replace(/[^0-9.-]/g, '');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') {
    return undefined;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatCurrencySummaryValue(symbol: string, value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }

  return `${symbol}${CURRENCY_NUMBER_FORMATTER.format(Number(value))}`;
}

function getCategoryOptionLabel(category: Category) {
  return category.category_code
    ? `${category.category_name} (${category.category_code})`
    : category.category_name;
}

function CurrencyAmountInput({
  symbol,
  value,
  onChange,
  placeholder = '0.00',
  prefixClassName = '',
  wrapperClassName = '',
  inputClassName = '',
  disabled = false,
}: CurrencyAmountInputProps) {
  const [inputValue, setInputValue] = useState(() => formatCurrencyInputValue(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      queueMicrotask(() => {
        setInputValue(formatCurrencyInputValue(value));
      });
    }
  }, [isFocused, value]);

  return (
    <div
      className={[
        'flex items-stretch overflow-hidden rounded-lg border bg-white dark:bg-gray-700',
        'border-gray-300 dark:border-gray-600 focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500',
        disabled ? 'opacity-60' : '',
        wrapperClassName,
      ].filter(Boolean).join(' ')}
    >
      <span
        className={[
          'inline-flex w-12 shrink-0 items-center justify-center border-r bg-gray-50 text-sm font-medium',
          'border-gray-200 text-gray-500 dark:border-gray-600 dark:bg-gray-800/70 dark:text-gray-300',
          prefixClassName,
        ].filter(Boolean).join(' ')}
      >
        {symbol}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={inputValue}
        onFocus={() => {
          setIsFocused(true);
          setInputValue(value == null || value === '' ? '' : Number(value).toFixed(2));
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          setInputValue(nextValue);
          onChange(parseCurrencyInputValue(nextValue));
        }}
        onBlur={() => {
          setIsFocused(false);
          const parsed = parseCurrencyInputValue(inputValue);
          setInputValue(formatCurrencyInputValue(parsed ?? value));
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={[
          'w-full min-w-0 border-0 bg-transparent px-4 py-2 text-right font-mono text-gray-900 dark:text-white',
          'placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed',
          inputClassName,
        ].filter(Boolean).join(' ')}
      />
    </div>
  );
}

function calculateMarkupPercentage(costPrice?: number, sellingPrice?: number) {
  if (costPrice == null || sellingPrice == null || costPrice <= 0) {
    return null;
  }

  return ((sellingPrice - costPrice) / costPrice) * 100;
}

function buildCategoryOptionLabelMap(categories: Category[]) {
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const labelCache = new Map<number, string>();

  const resolveLabel = (category: Category, seen = new Set<number>()): string => {
    const cached = labelCache.get(category.id);
    if (cached) {
      return cached;
    }

    const baseLabel = getCategoryOptionLabel(category);
    if (!category.parent_category_id || seen.has(category.id)) {
      labelCache.set(category.id, baseLabel);
      return baseLabel;
    }

    const parent = categoriesById.get(category.parent_category_id);
    if (!parent) {
      labelCache.set(category.id, baseLabel);
      return baseLabel;
    }

    const nextSeen = new Set(seen);
    nextSeen.add(category.id);

    const resolvedLabel = `${resolveLabel(parent, nextSeen)} › ${baseLabel}`;
    labelCache.set(category.id, resolvedLabel);
    return resolvedLabel;
  };

  return categories.reduce<Record<number, string>>((acc, category) => {
    acc[category.id] = resolveLabel(category);
    return acc;
  }, {});
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function InventoryItemModal({
  isOpen,
  onClose,
  onSave,
  existingItem,
  defaultSubtype = 'product',
  defaultTrackInventory,
  moduleContext = 'operations',
  defaultContactId,
  defaultContactName,
  accentColor,
  overlayZIndexClassName = 'z-50',
  panelZIndexClassName,
  readOnly = false,
  modalSize = 'default',
  placement = 'center',
}: InventoryItemModalProps) {
  const { clientBranding } = useTheme();
  const primaryColor = clientBranding?.primary_color || 'var(--theme-primary)';

  // Form state
  const [formData, setFormData] = useState<InventoryItemData>({
    title: '',
    description: '',
    item_type: 'inventory',
    item_subtype: defaultSubtype,
    status: 'active',
    currency: 'ZAR',
    track_inventory: defaultTrackInventory ?? true,
    unit_of_measure: 'unit',
    reorder_level: 10,
    reorder_quantity: 20,
    valuation_method: 'fifo',
    is_serialized: false,
    is_batch_tracked: false,
    has_expiry: false,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reference data
  const [categories, setCategories] = useState<Category[]>([]);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  // Active step in the wizard
  const [currentStep, setCurrentStep] = useState<InventoryStep>('basic');
  const [supplierContacts, setSupplierContacts] = useState<Array<{ id: number; name: string }>>([]);
  const [customerContacts, setCustomerContacts] = useState<Array<{ id: number; name: string; currency?: string | null }>>([]);

  // Inline category creation
  const [showCategoryCreate, setShowCategoryCreate] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Supplier mode: 'direct' = pick from contacts table (suppliers), 'external' = free text
  const [supplierMode, setSupplierMode] = useState<'direct' | 'external'>('external');

  // Draft restoration tracking — prevents the auto-save effect from
  // overwriting a just-restored draft with default state on the initial cycle.
  const [draftRestored, setDraftRestored] = useState(false);
  // True while the "resume draft?" confirm is open — blocks auto-save from
  // overwriting the stored draft and keeps stale form state hidden.
  const [draftPromptPending, setDraftPromptPending] = useState(false);
  // The storage key a loaded draft came from, so "Start Fresh" clears the
  // right scoped entry.
  const draftKeyRef = useRef<string | null>(null);

  const isCreateMode = !existingItem;
  // Existing items (edit or read-only view) use freely-selectable tabs instead
  // of the gated create wizard — every section is reachable at any time.
  const useTabs = Boolean(existingItem);

  const createEmptyForm = useCallback((): InventoryItemData => ({
    title: '',
    description: '',
    item_type: 'inventory',
    item_subtype: defaultSubtype,
    status: 'active',
    currency: 'ZAR',
    track_inventory: defaultTrackInventory ?? true,
    unit_of_measure: 'unit',
    reorder_level: 10,
    reorder_quantity: 20,
    valuation_method: 'fifo',
    is_serialized: false,
    is_batch_tracked: false,
    has_expiry: false,
    photo_url: null,
  }), [defaultSubtype, defaultTrackInventory]);

  const currencySymbol = getCurrencySymbol(formData.currency);
  const categoryOptionLabels = useMemo(() => buildCategoryOptionLabelMap(categories), [categories]);
  const markupPercentage = useMemo(
    () => calculateMarkupPercentage(formData.cost_price, formData.selling_price),
    [formData.cost_price, formData.selling_price],
  );
  const marketingMaterialLineItems = useMemo(() => {
    if (readOnly) {
      return [] as Array<{ category_id?: number }>;
    }

    const categoryId = existingItem?.category_id ?? formData.category_id;
    return categoryId ? [{ category_id: categoryId }] : [];
  }, [existingItem?.category_id, formData.category_id, readOnly]);
  const resolvedDefaultContactName = useMemo(() => {
    const explicitName = defaultContactName?.trim();
    if (explicitName) {
      return explicitName;
    }

    if (defaultContactId != null) {
      const matchedCustomer = customerContacts.find((contact) => contact.id === defaultContactId)?.name?.trim();
      if (matchedCustomer) {
        return matchedCustomer;
      }

      const matchedPricingName = formData.customer_pricing
        ?.find((pricing) => pricing.contact_id === defaultContactId)
        ?.contact_name
        ?.trim();
      if (matchedPricingName) {
        return matchedPricingName;
      }
    }

    const formContactName = formData.contact_name?.trim();
    if (formContactName) {
      return formContactName;
    }

    return null;
  }, [customerContacts, defaultContactId, defaultContactName, formData.contact_name, formData.customer_pricing]);

  const loadCategories = useCallback(async () => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const response = await fetch(`/api/v1/work-items/inventory/categories?applies_to=${encodeURIComponent(formData.item_subtype)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      if (data.success) {
        const nextCategories: Category[] = Array.isArray(data.data) ? (data.data as Category[]) : [];
        setCategories(nextCategories);
        setFormData((prev) => {
          if (!prev.category_id || nextCategories.some((category) => category.id === prev.category_id)) {
            return prev;
          }

          return {
            ...prev,
            category_id: undefined,
            category_name: undefined,
          };
        });
      }
    } catch (err) {
      console.error('Error loading inventory categories:', err);
    }
  }, [formData.item_subtype]);

  const handleCreateCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;

    setCreatingCategory(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const response = await fetch('/api/v1/work-items/inventory/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          category_name: trimmed,
          applies_to: formData.item_subtype === 'bundle' ? 'all' : formData.item_subtype,
          is_active: true,
          sort_order: 0,
        }),
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success && data.data?.id) {
        const created: Category = data.data;
        setCategories((prev) => [...prev, created]);
        setFormData((prev) => ({
          ...prev,
          category_id: created.id,
          category_name: created.category_name,
        }));
        setNewCategoryName('');
        setShowCategoryCreate(false);
      }
    } catch (err) {
      console.error('Error creating category:', err);
    } finally {
      setCreatingCategory(false);
    }
  };

  const loadReferenceData = async () => {
    try {
      setLoading(true);
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      // Load tax rates and warehouses in parallel
      const [taxRatesRes, warehousesRes] = await Promise.all([
        fetch('/api/v1/finance?action=getTaxRates', {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action: 'getTaxRates' })
        }),
        fetch('/api/v1/work-items/inventory/warehouses', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (taxRatesRes.ok) {
        const data = await taxRatesRes.json();
        if (data.success) {
          setTaxRates(data.data || []);
        }
      }

      if (warehousesRes.ok) {
        const data = await warehousesRes.json();
        if (data.success) {
          setWarehouses(data.data || []);
        }
      }

      // Load supplier + vendor contacts (for product Suppliers tab)
      try {
        const contactsRes = await fetch('/api/v1/contacts?limit=500', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (contactsRes.ok) {
          const cd = await contactsRes.json();
          const rows = Array.isArray(cd.data) ? cd.data : Array.isArray(cd.contacts) ? cd.contacts : [];
          const supplierRows = rows.filter((contact: ContactListRow) => {
            const contactType = String(contact.contact_type || '').toLowerCase();
            return contactType === 'supplier';
          });

          if (supplierRows.length > 0) {
            setSupplierContacts(
              supplierRows.map((c: ContactListRow) => ({
                id: c.id,
                name: `${c.company_name || c.contact_person || `Supplier #${c.id}`} · Supplier`,
              })),
            );
          } else {
            setSupplierContacts([]);
          }
        }
      } catch {
        // non-fatal
      }

      // Load customer contacts (for product Customer Pricing tab)
      try {
        const customerRes = await fetch('/api/v1/contacts?contact_type=customer&is_active=true&limit=1000', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (customerRes.ok) {
          const cd = await customerRes.json();
          const rows = Array.isArray(cd.data) ? cd.data : Array.isArray(cd.contacts) ? cd.contacts : [];
          if (rows.length > 0) {
            setCustomerContacts(
              rows.map((c: ContactListRow) => ({
                id: c.id,
                name: c.company_name || c.contact_person || c.email || `Customer #${c.id}`,
                currency: c.currency || null,
              })),
            );
          }
        }
      } catch {
        // non-fatal
      }
    } catch (err) {
      console.error('Error loading reference data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load reference data
  useEffect(() => {
    if (isOpen) {
      void loadReferenceData();
      void loadCategories();
    }
  }, [isOpen, loadCategories]);

  // Initialize form with existing item, or restore draft in create mode
  useEffect(() => {
    if (isOpen) {
      setShowCategoryCreate(false);
      setNewCategoryName('');
      setCreatingCategory(false);
    }

    if (existingItem) {
      setFormData({
        ...existingItem,
        item_type: 'inventory',
        item_subtype: existingItem.item_subtype || defaultSubtype,
        track_inventory: existingItem.track_inventory ?? true,
        custom_fields: existingItem.custom_fields || {},
        photo_url: existingItem.photo_url ?? null,
      });
      setSupplierMode(existingItem.supplier_id ? 'direct' : 'external');
      setDraftRestored(false);
      return;
    }

    // Create mode — check for a previously saved in-progress draft
    if (isOpen && isCreateMode) {
      const found = loadInventoryDraft(moduleContext);
      if (found) {
        draftKeyRef.current = found.key;
        // Reset to a blank form while the prompt is open so stale values from
        // a previous session aren't visible behind the confirm dialog.
        setFormData(createEmptyForm());
        setCurrentStep('basic');
        setSupplierMode('external');
        setDraftPromptPending(true);
        notify
          .confirm({
            title: 'Resume draft?',
            message: 'You have an in-progress inventory item draft. Would you like to resume where you left off, or start a new one?',
            confirmText: 'Resume Draft',
            cancelText: 'Start Fresh',
            variant: 'info',
          })
          .then((resume) => {
            setDraftPromptPending(false);
            if (resume) {
              setFormData(found.draft.formData);
              setCurrentStep(found.draft.currentStep || 'basic');
              setSupplierMode(found.draft.formData.supplier_id ? 'direct' : 'external');
              setDraftRestored(true);
            } else {
              if (draftKeyRef.current) {
                clearInventoryDraft(draftKeyRef.current);
                draftKeyRef.current = null;
              }
              setFormData(createEmptyForm());
              setSupplierMode('external');
              setDraftRestored(false);
            }
          });
        return;
      }
    }

    // No draft — fresh start
    setFormData(createEmptyForm());
    setSupplierMode('external');
    setDraftRestored(false);
  }, [existingItem, defaultSubtype, isOpen, isCreateMode, moduleContext, createEmptyForm]);

  useEffect(() => {
    if (!isOpen) return;

    const resolvedSubtype = existingItem?.item_subtype || defaultSubtype;
    if (readOnly && existingItem?.id && resolvedSubtype === 'product') {
      setCurrentStep('images');
      return;
    }

    setCurrentStep('basic');
  }, [defaultSubtype, existingItem?.id, existingItem?.item_subtype, isOpen, readOnly]);

  // ---- Auto-save draft to localStorage on step / form data changes (create mode only) ----
  useEffect(() => {
    if (!isOpen || !isCreateMode || readOnly || draftPromptPending) return;
    // Skip the initial render cycle when we just restored a draft — there's
    // nothing new to save and we don't want to overwrite the timestamp.
    if (draftRestored) {
      setDraftRestored(false);
      return;
    }
    saveInventoryDraft(
      draftStorageKey(moduleContext, formData.item_subtype || defaultSubtype),
      { formData, currentStep },
    );
  }, [isOpen, isCreateMode, readOnly, draftPromptPending, draftRestored, formData, currentStep, moduleContext, defaultSubtype]);

  // ---- Photo upload handler ----
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!STATIC_FILE_API_KEY) {
      notify.error('Photo uploads are not configured on this build');
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
      return;
    }
    setUploadingPhoto(true);
    const url = await uploadProductPhoto(file, 'product-photos');
    if (url) {
      setFormData(prev => ({ ...prev, photo_url: url }));
      setError(null);
      notify.success('Photo uploaded');
    } else {
      notify.error('Failed to upload photo');
    }
    setUploadingPhoto(false);
    // Reset the input so the same file can be re-selected
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const handleInputChange = <K extends keyof InventoryItemData>(field: K, value: InventoryItemData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const submitForm = async () => {
    // Validation
    if (!formData.title?.trim()) {
      setError('Title is required');
      // In tab mode the title field lives on the first tab — jump there so
      // the user can see and fix the problem instead of a hidden error.
      if (useTabs) {
        setCurrentStep('basic');
      }
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave(formData);
      // Clear whichever scoped draft slot this item occupied.
      clearInventoryDraft(draftStorageKey(moduleContext, formData.item_subtype || defaultSubtype));
      if (draftKeyRef.current && draftKeyRef.current !== draftStorageKey(moduleContext, formData.item_subtype || defaultSubtype)) {
        clearInventoryDraft(draftKeyRef.current);
      }
      draftKeyRef.current = null;
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  // Get subtype-specific configuration
  const getSubtypeConfig = (subtype: InventorySubtype) => {
    switch (subtype) {
      case 'product':
        return {
          icon: Package,
          color: '#f97316',
          label: 'Product',
          description: 'Physical inventory item with stock tracking',
          showStock: true,
          showTracking: true
        };
      case 'service':
        return {
          icon: Briefcase,
          color: '#3b82f6',
          label: 'Service',
          description: 'Time-based or labor service',
          showStock: false,
          showTracking: false
        };
      case 'bundle':
        return {
          icon: Layers,
          color: '#8b5cf6',
          label: 'Bundle',
          description: 'Collection of products/services sold together',
          showStock: false,
          showTracking: false
        };
    }
  };

  const config = getSubtypeConfig(formData.item_subtype);
  const Icon = config.icon;
  const resolvedAccentColor = accentColor || config.color;

  // ---- Steps definition ----
  // Products get the full wizard (basic → pricing → stock → details → review).
  // Services and bundles get a lightweight 2-step flow (basic → pricing → review).
  // Existing items also get edit-only steps (images, materials, suppliers, customer-pricing).
  const steps = useMemo<Array<{ key: InventoryStep; label: string; icon: React.ElementType; description: string }>>(() => {
    const isProduct = formData.item_subtype === 'product';
    const hasId = Boolean(existingItem?.id);

    if (isProduct) {
      const productSteps: Array<{ key: InventoryStep; label: string; icon: React.ElementType; description: string }> = [
        { key: 'basic', label: 'Basic Info', icon: Package, description: 'Name, SKU & category' },
        { key: 'pricing', label: 'Pricing', icon: DollarSign, description: 'Cost, selling price & tax' },
        { key: 'stock', label: 'Stock', icon: Box, description: 'Tracking & reorder settings' },
        { key: 'details', label: 'Details', icon: Tag, description: 'Manufacturer & brand' },
      ];
      if (hasId && !readOnly) {
        productSteps.push({ key: 'suppliers', label: 'Suppliers', icon: Truck, description: 'Multi-supplier mapping' });
        productSteps.push({ key: 'customer-pricing', label: 'Customer Pricing', icon: Users, description: 'Per-customer contracted prices' });
      }
      if (hasId) {
        productSteps.push({ key: 'images', label: 'Images', icon: ImageIcon, description: 'Product photos' });
        productSteps.push({ key: 'materials', label: 'Materials', icon: FileText, description: 'Brochures & collateral' });
      }
      productSteps.push({ key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Review & save' });
      return productSteps;
    }

    // Services & bundles: basic → pricing → review (+ materials for existing items)
    const simpleSteps: Array<{ key: InventoryStep; label: string; icon: React.ElementType; description: string }> = [
      { key: 'basic', label: 'Details', icon: config.icon, description: `${config.label} name & description` },
      { key: 'pricing', label: 'Pricing', icon: DollarSign, description: 'Cost & selling price' },
    ];
    if (hasId) {
      simpleSteps.push({ key: 'materials', label: 'Materials', icon: FileText, description: 'Brochures & collateral' });
    }
    simpleSteps.push({ key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Review & save' });
    return simpleSteps;
  }, [formData.item_subtype, existingItem?.id, readOnly, config.icon, config.label]);

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStep === 'review';

  // Emit a custom event when the wizard step changes so the AI onboarding
  // coach's page context observer can track which step the user is on.
  useEffect(() => {
    if (!isOpen) return;
    window.dispatchEvent(new CustomEvent('guided-tour-training-event', {
      detail: { type: `modal_step:${currentStep}` },
    }));
  }, [currentStep, isOpen]);

  // ---- Validation per step ----
  const validateStep = useCallback((step: InventoryStep): { ok: true } | { ok: false; firstError: string } => {
    if (readOnly) return { ok: true };

    if (step === 'basic') {
      if (!formData.title?.trim()) {
        return { ok: false, firstError: `${config.label} name is required` };
      }
    }

    return { ok: true };
  }, [formData.title, readOnly, config.label]);

  // ---- Navigation ----
  const handleNext = () => {
    const result = validateStep(currentStep);
    if (!result.ok) {
      setError(result.firstError);
      return;
    }
    setError(null);
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].key);
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].key);
    }
  };

  const goToStep = (step: InventoryStep) => {
    const targetIdx = steps.findIndex((s) => s.key === step);
    // Tab mode and read-only mode allow free navigation. Otherwise, going forward requires validation.
    if (useTabs || readOnly || targetIdx <= currentStepIndex) {
      setCurrentStep(step);
      setError(null);
    } else {
      // Validate all steps up to the target
      for (let i = 0; i < targetIdx; i++) {
        const result = validateStep(steps[i].key);
        if (!result.ok) {
          setError(result.firstError);
          setCurrentStep(steps[i].key);
          return;
        }
      }
      setError(null);
      setCurrentStep(step);
    }
  };

  // ---- Step bar ----
  const renderStepBar = () => (
    <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 overflow-x-auto">
      {steps.map((step, idx) => {
        const isCompleted = idx < currentStepIndex;
        const isCurrent = idx === currentStepIndex;
        const StepIcon = step.icon;

        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div className={`flex-shrink-0 w-8 h-px ${isCompleted ? 'bg-green-400' : 'bg-gray-300 dark:bg-gray-600'}`} />
            )}
            <button
              type="button"
              onClick={() => goToStep(step.key)}
              data-region={`inventory-modal-step-${step.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                ${isCurrent ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600' : ''}
                ${isCompleted ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer' : ''}
                ${!isCurrent && !isCompleted ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : ''}
              `}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                  ${isCompleted ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' : ''}
                  ${isCurrent ? 'text-white' : ''}
                  ${!isCurrent && !isCompleted ? 'bg-gray-100 dark:bg-gray-700 text-gray-400' : ''}
                `}
                style={isCurrent ? { backgroundColor: resolvedAccentColor } : undefined}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
              </div>
              <div className="hidden sm:block text-left">
                <div className="leading-tight">{step.label}</div>
                <div className="text-[10px] font-normal text-gray-400 dark:text-gray-500 leading-tight">{step.description}</div>
              </div>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  // ---- Tab bar (edit/view mode — every section selectable at any time) ----
  const renderTabBar = () => (
    <div
      role="tablist"
      aria-label={`${config.label} sections`}
      className="flex items-center gap-1 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 overflow-x-auto"
    >
      {steps.map((tab) => {
        const isActive = currentStep === tab.key;
        const TabIcon = tab.icon;

        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => goToStep(tab.key)}
            data-region={`inventory-modal-step-${tab.key}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
              ${isActive
                ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-gray-600'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white'}
            `}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0
                ${isActive ? 'text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}
              `}
              style={isActive ? { backgroundColor: resolvedAccentColor } : undefined}
            >
              <TabIcon className="w-4 h-4" />
            </div>
            <div className="hidden sm:block text-left">
              <div className="leading-tight">{tab.label}</div>
              <div className="text-[10px] font-normal text-gray-400 dark:text-gray-500 leading-tight">{tab.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );

  if (!isOpen) return null;

  const isRightPanel = placement === 'right';
  // 'search-match' mirrors ItemSearchModal's right-anchored max-w-5xl panel so
  // the create/preview modal visually replaces the picker instead of covering it.
  const panelWidthClass = modalSize === 'search-match'
    ? 'w-full max-w-full sm:max-w-5xl'
    : 'w-full max-w-full sm:max-w-3xl';

  const modalBody = (
    <>
      {/* Header */}
      <div className="mb-3 sm:mb-6 px-2 relative">
        <div className="text-center">
          <div
            className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-white/25 to-black/10 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16"
            style={{ backgroundColor: resolvedAccentColor }}
          >
            <Icon className="h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">
            {existingItem ? `${readOnly ? 'View' : 'Edit'} ${config.label}` : `New ${config.label}`}
            <UserDocsLink docsPath="/total-operations/workflows/asset-inventory-management/inventory#creating-an-item" label="User guide: Parts & Inventory" className="ml-1 align-middle" />
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {readOnly ? `${config.label} details (read-only)` : config.description}
          </p>
          {readOnly && (
            <span className="inline-block mt-2 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              Read only
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Step Bar (create wizard) / Tab Bar (edit & view) */}
      <div className={isRightPanel ? 'shrink-0' : 'max-w-5xl mx-auto w-full shrink-0'}>
        {useTabs ? renderTabBar() : renderStepBar()}
      </div>

      {/* Form Content */}
      <form
        onSubmit={(e) => e.preventDefault()}
        className={isRightPanel
          ? 'flex-1 flex flex-col overflow-hidden min-h-0'
          : 'max-w-5xl mx-auto w-full flex-1'
        }
      >
        <div className={isRightPanel
          ? 'flex-1 overflow-y-auto px-6 py-5 space-y-6'
          : 'px-2 sm:px-6 py-5 space-y-6'
        }>
            {/* Error Display */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {(readOnly || defaultContactId) && (
              <div className="grid gap-2 md:grid-cols-2">
                {readOnly && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        <Eye className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Preview mode</p>
                        <p className="text-sm font-medium">Viewing the full item specification</p>
                        <p className="mt-0.5 text-[11px] leading-4 text-blue-700 dark:text-blue-300">
                          Use the {useTabs ? 'tabs' : 'steps'} to review pricing, stock, specs, and linked brochures or reference material.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {defaultContactId && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        <UserRound className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Customer context</p>
                        <p className="truncate text-sm font-medium">
                          {resolvedDefaultContactName || `Contact #${defaultContactId}`}
                        </p>
                        <p className="mt-0.5 text-[11px] leading-4 text-blue-700 dark:text-blue-300">
                          The pricing step can show or set contracted pricing for this customer.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <fieldset disabled={readOnly} className="contents">

            {/* Basic Info Step */}
            {currentStep === 'basic' && (
              <div className="space-y-4">
                {/* Photo + Type + Title */}
                <div className="flex items-start gap-6">
                  {/* Photo Upload */}
                  <div className="flex-shrink-0">
                    <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    <div
                      onClick={() => !readOnly && photoInputRef.current?.click()}
                      className={`w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden transition-colors group ${
                        readOnly ? 'cursor-default' : 'hover:border-blue-500 cursor-pointer'
                      }`}
                    >
                      {formData.photo_url ? (
                        /* eslint-disable-next-line @next/next/no-img-element */
                        <img src={formData.photo_url} alt="Item photo" className="w-full h-full object-cover" />
                      ) : uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      ) : (
                        <div className="text-center">
                          <Upload className="w-5 h-5 text-gray-400 group-hover:text-blue-500 mx-auto" />
                          <span className="text-[10px] text-gray-400 mt-1">Photo</span>
                        </div>
                      )}
                    </div>
                    {formData.photo_url && !readOnly && (
                      <button
                        type="button"
                        onClick={() => handleInputChange('photo_url', null)}
                        className="mt-1 w-full text-[10px] text-red-500 hover:text-red-600 dark:text-red-400"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="flex-1 space-y-4">
                    {/* Item Type Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Item Type
                      </label>
                      <select
                        value={formData.item_subtype}
                        onChange={(e) => handleInputChange('item_subtype', e.target.value as InventorySubtype)}
                        disabled={readOnly || Boolean(existingItem?.id)}
                        className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <option value="product">Product — Physical inventory item with stock tracking</option>
                        <option value="service">Service — Time-based or labor service</option>
                        <option value="bundle">Bundle — Collection of products/services sold together</option>
                      </select>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {formData.item_subtype === 'product' ? 'Product Name' : formData.item_subtype === 'service' ? 'Service Name' : 'Bundle Name'} *
                      </label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder={`Enter ${formData.item_subtype} name`}
                        className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        data-region="inventory-modal-field-product-name"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter description..."
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* SKU and Barcode Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Tag className="w-4 h-4 inline mr-1" />
                      SKU
                    </label>
                    <input
                      type="text"
                      value={formData.sku || ''}
                      onChange={(e) => handleInputChange('sku', e.target.value)}
                      placeholder="e.g., PROD-001"
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-region="inventory-modal-field-sku"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Barcode className="w-4 h-4 inline mr-1" />
                      Barcode
                    </label>
                    <input
                      type="text"
                      value={formData.barcode || ''}
                      onChange={(e) => handleInputChange('barcode', e.target.value)}
                      placeholder="e.g., 6001234567890"
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Category and Status Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Category
                      </label>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => setShowCategoryCreate((prev) => !prev)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Plus size={12} />
                          {showCategoryCreate ? 'Cancel' : 'New'}
                        </button>
                      )}
                    </div>
                    {showCategoryCreate ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          placeholder="Category name..."
                          className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleCreateCategory();
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => void handleCreateCategory()}
                          disabled={creatingCategory || !newCategoryName.trim()}
                          className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {creatingCategory ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Add'
                          )}
                        </button>
                      </div>
                    ) : (
                      <select
                        value={formData.category_id || ''}
                        onChange={(e) => {
                          const categoryId = e.target.value ? parseInt(e.target.value, 10) : undefined;
                          const selectedCategory = categories.find((category) => category.id === categoryId);
                          setFormData((prev) => ({
                            ...prev,
                            category_id: categoryId,
                            category_name: selectedCategory?.category_name,
                          }));
                          setError(null);
                        }}
                        className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{categoryOptionLabels[cat.id] || getCategoryOptionLabel(cat)}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status || 'active'}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="discontinued">Discontinued</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing Section (always visible for services/bundles, tab for products) */}
            {currentStep === 'pricing' && (
              <div className="space-y-4">
                {formData.item_subtype === 'product' && (
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pricing Information
                  </h3>
                )}

                {/* Cost and Selling Price */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Cost Price
                    </label>
                    <CurrencyAmountInput
                      symbol={currencySymbol}
                      value={formData.cost_price || ''}
                      onChange={(value) => handleInputChange('cost_price', value)}
                    />
                  </div>
                  <div data-region="inventory-modal-field-selling-price">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Selling Price
                    </label>
                    <CurrencyAmountInput
                      symbol={currencySymbol}
                      value={formData.selling_price || ''}
                      onChange={(value) => handleInputChange('selling_price', value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Markup %
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        value={markupPercentage != null ? markupPercentage.toFixed(2) : ''}
                        onChange={(event) => {
                          const nextMarkup = event.target.value ? parseFloat(event.target.value) : undefined;
                          const costPrice = formData.cost_price;

                          if (nextMarkup == null) {
                            handleInputChange('selling_price', undefined);
                            return;
                          }

                          if (costPrice == null || costPrice <= 0) {
                            setError('Set a cost price before calculating markup.');
                            return;
                          }

                          handleInputChange('selling_price', Number((costPrice * (1 + nextMarkup / 100)).toFixed(2)));
                        }}
                        placeholder="0.00"
                        disabled={formData.cost_price == null || formData.cost_price <= 0}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800/60 dark:disabled:text-gray-500"
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-center text-sm font-medium text-gray-500 dark:text-gray-300">%</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Enter a markup to auto-calculate the selling price from the cost price.
                    </p>
                  </div>
                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium">Pricing summary</span>
                      <span>{currencySymbol}</span>
                    </div>
                    <div className="mt-2 space-y-1 text-xs">
                      <div className="flex items-center justify-between gap-3">
                        <span>Cost</span>
                        <span>{formatCurrencySummaryValue(currencySymbol, formData.cost_price)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>Selling</span>
                        <span>{formatCurrencySummaryValue(currencySymbol, formData.selling_price)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>Markup</span>
                        <span>{markupPercentage != null ? `${markupPercentage.toFixed(1)}%` : '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Margin Display */}
                {formData.cost_price && formData.selling_price && formData.selling_price > 0 && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Gross Margin:</span>
                      <span className={`font-medium ${
                        ((formData.selling_price - formData.cost_price) / formData.selling_price * 100) > 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {((formData.selling_price - formData.cost_price) / formData.selling_price * 100).toFixed(1)}%
                        {' '}({formatCurrencySummaryValue(currencySymbol, formData.selling_price - formData.cost_price)})
                      </span>
                    </div>
                  </div>
                )}

                {/* Customer-specific price for context customer */}
                {defaultContactId && (
                  <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
                    <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                      Customer Price{resolvedDefaultContactName ? ` for ${resolvedDefaultContactName}` : ''}
                    </label>
                    <CurrencyAmountInput
                      symbol={currencySymbol}
                      value={formData.customer_pricing?.find(cp => cp.contact_id === defaultContactId)?.selling_price ?? ''}
                      onChange={(val) => {
                        setFormData(prev => {
                          const existing = prev.customer_pricing?.filter(cp => cp.contact_id !== defaultContactId) ?? [];
                          return {
                            ...prev,
                            customer_pricing: val != null
                              ? [...existing, { contact_id: defaultContactId, contact_name: resolvedDefaultContactName || undefined, selling_price: val }]
                              : existing,
                          };
                        });
                      }}
                      placeholder="Leave blank to use standard price"
                      wrapperClassName="border-blue-300 dark:border-blue-600 focus-within:ring-blue-500"
                      prefixClassName="border-blue-200 bg-blue-100 text-blue-600 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                    />
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Sets a contracted price for this customer when this item is used in their billing.
                    </p>
                  </div>
                )}

                {/* Tax Rate */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Percent className="w-4 h-4 inline mr-1" />
                      Tax Rate
                    </label>
                    <select
                      value={formData.tax_rate_id || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value ? parseInt(e.target.value, 10) : undefined;
                        const selectedRate = taxRates.find(r => r.id === selectedId);
                        handleInputChange('tax_rate_id', selectedId);
                        if (selectedRate) {
                          handleInputChange('tax_rate', selectedRate.rate);
                        }
                      }}
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">No tax</option>
                      {taxRates.map(rate => (
                        <option key={rate.id} value={rate.id}>{rate.name} ({rate.rate}%)</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Currency
                    </label>
                    <select
                      value={formData.currency || 'ZAR'}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="ZAR">ZAR - South African Rand</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Stock Settings Section (products only) */}
            {formData.item_subtype === 'product' && currentStep === 'stock' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Stock Settings
                </h3>

                {/* Track Inventory Toggle */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.track_inventory ?? true}
                      onChange={(e) => handleInputChange('track_inventory', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Track inventory for this product</span>
                      <p className="text-xs text-gray-500">When enabled, stock levels are tracked and movements are recorded. Disable for special-order or non-stocked items.</p>
                    </div>
                  </label>
                </div>

                {/* Unit of Measure and Reorder Level */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <Scale className="w-4 h-4 inline mr-1" />
                      Unit of Measure
                    </label>
                    <select
                      value={formData.unit_of_measure || 'unit'}
                      onChange={(e) => handleInputChange('unit_of_measure', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="unit">Unit</option>
                      <option value="each">Each</option>
                      <option value="box">Box</option>
                      <option value="pack">Pack</option>
                      <option value="case">Case</option>
                      <option value="pallet">Pallet</option>
                      <option value="kg">Kilogram (kg)</option>
                      <option value="g">Gram (g)</option>
                      <option value="l">Litre (L)</option>
                      <option value="ml">Millilitre (ml)</option>
                      <option value="m">Metre (m)</option>
                      <option value="cm">Centimetre (cm)</option>
                      <option value="sqm">Square Metre (sqm)</option>
                      <option value="hour">Hour</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      Reorder Level
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.reorder_level || ''}
                      onChange={(e) => handleInputChange('reorder_level', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="10"
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this level</p>
                  </div>
                </div>

                {/* Valuation Method */}
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <Layers className="w-4 h-4 inline mr-1" />
                    Inventory Valuation Method
                  </label>
                  <select
                    value={formData.valuation_method || 'fifo'}
                    onChange={(e) => handleInputChange('valuation_method', e.target.value as 'fifo' | 'weighted_average' | 'lifo' | 'standard' | 'specific')}
                    className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="fifo">FIFO — First In, First Out</option>
                    <option value="weighted_average">Moving Average Cost (Weighted Average)</option>
                    <option value="lifo">LIFO — Last In, First Out</option>
                    <option value="standard">Standard Cost — Fixed cost per unit</option>
                    <option value="specific">Specific Identification — Track individual items</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Determines how the cost of goods sold and ending inventory value are calculated. FIFO assumes oldest stock is sold first. Moving Average recalculates the average cost after each purchase. Standard uses a fixed cost per unit. Specific tracks individual serial/batch items.
                  </p>
                </div>

                {(formData.track_inventory ?? true) && (
                  <>
                    {/* Reorder Quantity */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Reorder Quantity
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.reorder_quantity || ''}
                          onChange={(e) => handleInputChange('reorder_quantity', e.target.value ? parseInt(e.target.value) : undefined)}
                          placeholder="20"
                          className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">Suggested quantity for purchase orders</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          <Box className="w-4 h-4 inline mr-1" />
                          Weight (kg)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.weight || ''}
                          onChange={(e) => handleInputChange('weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                          placeholder="0.00"
                          className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Tracking Options */}
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Tracking Options</h4>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.is_serialized || false}
                            onChange={(e) => handleInputChange('is_serialized', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Serial Number Tracking</span>
                            <p className="text-xs text-gray-500">Track individual items by serial number</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.is_batch_tracked || false}
                            onChange={(e) => handleInputChange('is_batch_tracked', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Batch/Lot Tracking</span>
                            <p className="text-xs text-gray-500">Track items by batch or lot number</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.has_expiry || false}
                            onChange={(e) => handleInputChange('has_expiry', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Expiry Date Tracking</span>
                            <p className="text-xs text-gray-500">Track expiry dates for perishables</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  </>
                )}
                {!(formData.track_inventory ?? true) && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Inventory tracking is disabled for this product. Stock levels and movements will not be recorded. This is suitable for special-order items or non-stocked products.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Details Section (products only) */}
            {formData.item_subtype === 'product' && currentStep === 'details' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Product Details
                </h3>

                {/* Manufacturer and Brand */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Manufacturer
                    </label>
                    <input
                      type="text"
                      value={formData.manufacturer || ''}
                      onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                      placeholder="e.g., Acme Corp"
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Brand
                    </label>
                    <input
                      type="text"
                      value={formData.brand || ''}
                      onChange={(e) => handleInputChange('brand', e.target.value)}
                      placeholder="e.g., Premium Line"
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Supplier — direct (from contacts) or external (free text) */}
                <div className="space-y-3">
                  {/* Toggle: Direct vs External */}
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg w-fit">
                    <button
                      type="button"
                      onClick={() => {
                        setSupplierMode('direct');
                        // Clear external free-text supplier name when switching to direct
                        if (formData.supplier_name && formData.supplier_id == null) {
                          handleInputChange('supplier_name', undefined);
                        }
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        supplierMode === 'direct'
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <Truck className="w-3.5 h-3.5" />
                      Direct Supplier
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSupplierMode('external');
                        // Clear direct supplier reference when switching to external
                        if (formData.supplier_id != null) {
                          handleInputChange('supplier_id', undefined);
                          handleInputChange('supplier_name', undefined);
                        }
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        supplierMode === 'external'
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <Tag className="w-3.5 h-3.5" />
                      External Supplier
                    </button>
                  </div>

                  {/* Direct supplier — searchable dropdown from contacts table */}
                  {supplierMode === 'direct' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Supplier
                      </label>
                      <ContactSearchFilter
                        value={formData.supplier_id ? {
                          value: formData.supplier_id,
                          label: formData.supplier_name || `Supplier #${formData.supplier_id}`,
                          contact_type: 'supplier',
                        } : null}
                        onChange={(contact: ContactOption | null) => {
                          handleInputChange('supplier_id', contact?.value);
                          handleInputChange('supplier_name', contact?.label);
                        }}
                        contactTypes={['supplier']}
                        placeholder="Search suppliers..."
                        size="md"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Select from your existing supplier contacts.
                      </p>
                    </div>
                  )}

                  {/* External supplier — free text */}
                  {supplierMode === 'external' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Supplier Name
                      </label>
                      <input
                        type="text"
                        value={formData.supplier_name || ''}
                        onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                        placeholder="e.g., Acme Distributors"
                        className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Free-text supplier name for external or ad-hoc vendors not in your contacts.
                      </p>
                    </div>
                  )}

                  {/* Supplier SKU — always available regardless of mode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Supplier SKU
                    </label>
                    <input
                      type="text"
                      value={formData.supplier_sku || ''}
                      onChange={(e) => handleInputChange('supplier_sku', e.target.value)}
                      placeholder="Supplier's item code"
                      className="w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            </fieldset>

            {/* Suppliers Section (existing products only — multi-supplier mapping) */}
            {!readOnly && formData.item_subtype === 'product' && currentStep === 'suppliers' && existingItem?.id && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Suppliers
                </h3>
                <p className="text-xs text-gray-500">
                  Map this product to one or more suppliers with per-supplier SKU, cost, lead time, and MOQ. Marked supplier becomes the default for purchase orders.
                </p>
                <WorkItemSuppliersPanel
                  workItemId={String(existingItem.id)}
                  productName={formData.title}
                  supplierContacts={supplierContacts}
                />
              </div>
            )}

            {/* Customer Pricing Section (existing items only — per-customer contracted prices) */}
            {/* Images Section (existing products only) */}
            {formData.item_subtype === 'product' && currentStep === 'images' && existingItem?.id && (
              <ProductImagesTab
                workItemId={String(existingItem.id)}
                isActive={true}
                readOnly={readOnly}
              />
            )}

            {!readOnly && currentStep === 'customer-pricing' && existingItem?.id && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Customer-Specific Pricing
                </h3>
                <p className="text-xs text-gray-500">
                  Lock in contracted prices for individual customers. The pricing resolver uses these first, before any
                  assigned price list, on quotes, sales orders and invoices.
                </p>
                <WorkItemCustomerPricingPanel
                  workItemId={String(existingItem.id)}
                  productName={formData.title}
                  baseSellingPrice={formData.selling_price ?? null}
                  baseCurrency={formData.currency || 'ZAR'}
                  customerContacts={customerContacts}
                />
              </div>
            )}

            {formData.item_subtype === 'product' && currentStep === 'materials' && existingItem?.id && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Marketing Materials
                </h3>
                <p className="text-xs text-gray-500">
                  Review brochures, videos, white papers, freight info, and other collateral linked to this item.
                </p>
                <MarketingMaterialsPanel
                  workItemId={String(existingItem.id)}
                  lineItems={marketingMaterialLineItems}
                  isNewDocument={false}
                  readOnly={readOnly}
                />
              </div>
            )}

            {formData.item_subtype !== 'product' && currentStep === 'materials' && existingItem?.id && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Marketing Materials
                </h3>
                <p className="text-xs text-gray-500">
                  Review brochures, service literature, white papers, freight info, and other linked supporting material.
                </p>
                <MarketingMaterialsPanel
                  workItemId={String(existingItem.id)}
                  lineItems={marketingMaterialLineItems}
                  isNewDocument={false}
                  readOnly={readOnly}
                />
              </div>
            )}

            {/* Review Step */}
            {currentStep === 'review' && (
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Review &amp; {readOnly ? 'Summary' : 'Save'}
                </h3>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Basic info summary */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 space-y-2">
                    <div className="flex items-start gap-3">
                      {formData.photo_url && (
                        /* eslint-disable-next-line @next/next/no-img-element */
                        <img src={formData.photo_url} alt={formData.title || 'Item'} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                          <Icon className="w-4 h-4 flex-shrink-0" style={{ color: resolvedAccentColor }} />
                          <span className="truncate">{formData.title || 'Untitled'}</span>
                          {formData.sku && (
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">· SKU: {formData.sku}</span>
                          )}
                        </div>
                        {formData.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{formData.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 text-xs mt-2">
                          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300 capitalize">
                            {formData.item_subtype}
                          </span>
                          {formData.category_name && (
                            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {formData.category_name}
                            </span>
                          )}
                          <span className={`rounded-full px-2 py-0.5 ${formData.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {formData.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing summary */}
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Cost Price</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatCurrencySummaryValue(currencySymbol, formData.cost_price)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Selling Price</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatCurrencySummaryValue(currencySymbol, formData.selling_price)}</span>
                    </div>
                    {markupPercentage != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Markup</span>
                        <span className="font-medium text-gray-900 dark:text-white">{markupPercentage.toFixed(1)}%</span>
                      </div>
                    )}
                    {formData.tax_rate != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Tax Rate</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formData.tax_rate}%</span>
                      </div>
                    )}
                  </div>

                  {/* Stock summary (products only) */}
                  {formData.item_subtype === 'product' && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-1.5 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Inventory Tracking</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formData.track_inventory ? 'Enabled' : 'Disabled'}</span>
                      </div>
                      {formData.track_inventory && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Unit of Measure</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formData.unit_of_measure || 'unit'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Reorder Level</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formData.reorder_level ?? '—'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Valuation Method</span>
                            <span className="font-medium text-gray-900 dark:text-white uppercase">{(formData.valuation_method || 'fifo').replace('_', ' ')}</span>
                          </div>
                          {(formData.is_serialized || formData.is_batch_tracked || formData.has_expiry) && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500 dark:text-gray-400">Tracking</span>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {[
                                  formData.is_serialized && 'Serial',
                                  formData.is_batch_tracked && 'Batch',
                                  formData.has_expiry && 'Expiry',
                                ].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Details summary (products only) */}
                  {formData.item_subtype === 'product' && (formData.manufacturer || formData.brand || formData.supplier_name || formData.supplier_sku || formData.weight != null) && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-1.5 text-sm">
                      {formData.manufacturer && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Manufacturer</span>
                          <span className="font-medium text-gray-900 dark:text-white">{formData.manufacturer}</span>
                        </div>
                      )}
                      {formData.brand && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Brand</span>
                          <span className="font-medium text-gray-900 dark:text-white">{formData.brand}</span>
                        </div>
                      )}
                      {formData.supplier_name && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400">
                            Supplier{formData.supplier_id ? ' (Direct)' : ' (External)'}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">{formData.supplier_name}</span>
                        </div>
                      )}
                      {formData.supplier_sku && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Supplier SKU</span>
                          <span className="font-mono text-xs font-medium text-gray-900 dark:text-white">{formData.supplier_sku}</span>
                        </div>
                      )}
                      {formData.weight != null && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Weight</span>
                          <span className="font-medium text-gray-900 dark:text-white">{formData.weight} kg</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!readOnly && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Review the details above, then click {existingItem ? 'Update' : 'Create'} to save. Use the {useTabs ? 'tabs' : 'step bar'} above to navigate back and make changes.
                  </p>
                )}
              </div>
            )}
          </div>
      </form>

      {/* Footer — wizard navigation (create) or persistent actions (tabs) */}
      <ModalFooter
        className={isRightPanel ? 'w-full' : 'max-w-5xl mx-auto'}
        start={!useTabs && !isFirstStep && !readOnly ? (
          <button
            type="button"
            onClick={handleBack}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        ) : undefined}
      >
        <button
          type="button"
          onClick={onClose}
          className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          {readOnly ? 'Close' : 'Cancel'}
        </button>
        {readOnly ? (
          null
        ) : useTabs || isLastStep ? (
          <button
            type="button"
            onClick={() => void submitForm()}
            disabled={saving || !formData.title?.trim()}
            data-region="inventory-modal-save-button"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: saving ? undefined : resolvedAccentColor }}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {existingItem ? 'Update' : 'Create'} {config.label}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleNext}
            disabled={saving}
            data-region="inventory-modal-next-button"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: resolvedAccentColor }}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </ModalFooter>
    </>
  );

  if (isRightPanel) {
    return (
      <div
        className={`fixed inset-0 ${overlayZIndexClassName} overflow-hidden bg-black/50 backdrop-blur-sm`}
        data-region="inventory-item-modal"
        data-modal-step={currentStep}
        role="dialog"
        aria-modal="true"
        aria-label={existingItem ? `Edit ${config.label}` : `New ${config.label}`}
      >
        <div className="flex h-full justify-end">
          <div
            className={`relative h-full bg-gray-50 dark:bg-gray-900 flex flex-col shadow-2xl ${panelWidthClass} ${panelZIndexClassName ?? ''}`}
            style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="flex flex-col flex-1 min-h-0 px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
              {modalBody}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 ${overlayZIndexClassName} bg-gray-50 dark:bg-gray-900 overflow-y-auto`}
      data-region="inventory-item-modal"
      data-modal-step={currentStep}
      role="dialog"
      aria-modal="true"
      aria-label={existingItem ? `Edit ${config.label}` : `New ${config.label}`}
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex min-h-full flex-col px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {modalBody}
      </div>
    </div>
  );
}
